import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { fileToDataUrl, uploadInChunks } from "../../lib/mediaUpload";

// Admin → Upload Artist KYC: add KYC an artist sent offline (e.g. their
// declaration video on WhatsApp) to their record. A complete record goes to
// the KYC Queue for the usual review and approval.
const DOC_FIELDS = [
  ["aadhaar", "Aadhaar"],
  ["bank_proof", "Bank proof (cheque / passbook)"],
  ["pan", "PAN (optional)"],
  ["selfie", "Selfie (optional)"],
];
const DOC_LABELS = { aadhaar: "Aadhaar", bank_proof: "bank proof", pan: "PAN", selfie: "selfie", declaration_video: "declaration video", declarations: "declaration confirmation" };
const MAX_DOC_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const EMPTY_FORM = { full_name: "", dob: "", aadhaar_number: "", pan_number: "", note: "" };

const readVideoDuration = (file) => new Promise((resolve) => {
  const video = document.createElement("video");
  const url = URL.createObjectURL(file);
  const finish = (value) => { URL.revokeObjectURL(url); resolve(Number.isFinite(value) ? value : 0); };
  video.preload = "metadata";
  video.onloadedmetadata = () => {
    if (Number.isFinite(video.duration)) return finish(video.duration);
    video.ontimeupdate = () => { video.ontimeupdate = null; finish(video.duration); };
    video.currentTime = Number.MAX_SAFE_INTEGER;
  };
  video.onerror = () => finish(0);
  video.src = url;
});

export default function AdminKycUpload() {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [artist, setArtist] = useState(null);
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [docs, setDocs] = useState({});
  const [video, setVideo] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!query.trim() || artist) { setResults([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      api.get("/admin/artists", { params: { search: query.trim(), limit: 10 } })
        .then(({ data }) => { if (!cancelled) setResults(data?.items || []); })
        .catch((error) => { if (!cancelled) toast(formatApiError(error), "error"); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, artist, toast]);

  const loadRecord = async (userId) => {
    try {
      const { data } = await api.get(`/admin/kyc/${userId}/record`);
      setRecord(data);
    } catch (error) {
      toast(formatApiError(error), "error");
    }
  };

  const pick = (item) => {
    setArtist(item);
    setResults([]);
    setForm(EMPTY_FORM);
    setDocs({});
    setVideo(null);
    setConfirmed(false);
    loadRecord(item.user_id);
  };

  const chooseDoc = (key, file) => {
    if (!file) return;
    if (!/^image\/|^application\/pdf$/.test(file.type)) { toast("Documents must be an image or PDF.", "error"); return; }
    if (file.size > MAX_DOC_BYTES) { toast("Each document must be 5 MB or smaller.", "error"); return; }
    setDocs((current) => ({ ...current, [key]: file }));
  };

  const chooseVideo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast("Choose a video file.", "error"); return; }
    if (file.size > MAX_VIDEO_BYTES) { toast("The KYC video must be 100 MB or smaller.", "error"); return; }
    const duration = await readVideoDuration(file);
    if (!duration) { toast("Couldn't read this video's length. Try another file.", "error"); return; }
    if (duration > 120) { toast("The KYC video must be 2 minutes or shorter.", "error"); return; }
    setVideo({ file, duration });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!artist) return;
    if (confirmed && !video && !record?.has_video) { toast("Add the declaration video to confirm the declarations.", "error"); return; }
    setSaving(true);
    setProgress(0);
    try {
      let declarationVideoId;
      if (video) {
        const uploaded = await uploadInChunks(video.file, {
          type: "kyc_video",
          title: `KYC declaration (uploaded by admin) · ${artist.stage_name || ""}`.trim(),
          durationSeconds: Math.min(119.5, Math.max(1, video.duration)),
          onProgress: (fraction) => setProgress(Math.round(fraction * 100)),
        });
        declarationVideoId = uploaded.id;
      }
      const payload = { ...form, declarations_confirmed: confirmed };
      if (declarationVideoId) payload.declaration_video_id = declarationVideoId;
      for (const [key] of DOC_FIELDS) {
        if (docs[key]) payload[key] = await fileToDataUrl(docs[key]);
      }
      const { data } = await api.post(`/admin/kyc/${artist.user_id}/upload`, payload);
      toast(
        data.sent_for_review
          ? "KYC saved and sent to the KYC Queue for review."
          : `KYC saved. Still missing: ${data.missing.map((key) => DOC_LABELS[key] || key).join(", ")}.`,
        "success",
      );
      setForm(EMPTY_FORM);
      setDocs({});
      setVideo(null);
      setConfirmed(false);
      await loadRecord(artist.user_id);
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setSaving(false);
      setProgress(0);
    }
  };

  const field = (key, label, props = {}) => (
    <div>
      <label className="field-label" htmlFor={`admin-kyc-${key}`}>{label}</label>
      <input
        id={`admin-kyc-${key}`}
        className="field-input"
        value={form[key]}
        onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
        {...props}
      />
    </div>
  );

  return (
    <div className="pad-24" data-testid="admin-kyc-upload">
      <h2 className="font-serif fs-24 fw-700 mb-8">Upload Artist KYC</h2>
      <p className="text-muted fs-13 mb-16">
        Add KYC an artist sent you directly, such as their declaration video or documents. Existing documents are kept unless you upload a replacement.
        Once the video, Aadhaar, bank proof and declaration confirmation are all on record, it goes to the KYC Queue for approval.
      </p>

      <div className="card card-pad mb-16">
        <label className="field-label" htmlFor="admin-kyc-artist-search">Artist</label>
        {artist ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <div>
              <strong>{artist.stage_name || "Unnamed artist"}</strong>
              <span className="text-muted fs-12"> · {[artist.category, artist.city].filter(Boolean).join(" · ")}</span>
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setArtist(null); setRecord(null); setQuery(""); }}>
              Change artist
            </button>
          </div>
        ) : (
          <>
            <input
              id="admin-kyc-artist-search"
              className="field-input"
              placeholder="Search by stage name, email or phone"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              data-testid="admin-kyc-artist-search"
            />
            {results.length > 0 && (
              <div style={{ display: "grid", gap: "var(--space-1)", marginTop: "var(--space-2)" }}>
                {results.map((item) => (
                  <button
                    key={item.user_id}
                    type="button"
                    className="card"
                    onClick={() => pick(item)}
                    style={{ textAlign: "left", padding: "var(--space-2) var(--space-3)", cursor: "pointer" }}
                    data-testid={`admin-kyc-pick-${item.user_id}`}
                  >
                    <strong>{item.stage_name || "Unnamed artist"}</strong>
                    <span className="text-muted fs-12"> · {[item.category, item.city, (item.kyc_status || "").replace(/_/g, " ")].filter(Boolean).join(" · ")}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {artist && record && (
        <form className="card card-pad" onSubmit={submit} data-testid="admin-kyc-upload-form">
          <div className="mb-16 fs-13" data-testid="admin-kyc-record">
            <div><b>Status:</b> {(record.artist.kyc_status || "kyc_pending").replace(/_/g, " ")}{record.status ? ` · submission ${record.status.replace(/_/g, " ")}` : ""}</div>
            <div><b>On file:</b> {[...record.documents.map((key) => DOC_LABELS[key] || key), record.has_video && "declaration video"].filter(Boolean).join(", ") || "nothing yet"}</div>
            {(record.missing.length > 0 || !record.declarations_recorded) && (
              <div className="text-red">
                <b>Missing:</b> {[...record.missing, ...(record.declarations_recorded ? [] : ["declarations"])].map((key) => DOC_LABELS[key] || key).join(", ")}
              </div>
            )}
            {record.uploaded_by_admin && (
              <div className="text-muted fs-12">Last admin upload by {record.uploaded_by_admin.email} on {new Date(record.uploaded_by_admin.at).toLocaleString("en-IN")}</div>
            )}
          </div>

          <h3 className="fs-16 fw-700 mb-8">Declaration video</h3>
          <input type="file" accept="video/*" onChange={(event) => chooseVideo(event.target.files?.[0])} data-testid="admin-kyc-video" />
          <p className="text-muted fs-11 mt-4 mb-12">Up to 2 minutes and 100 MB. {video ? `Selected: ${video.file.name} (${Math.round(video.duration)} s)` : record.has_video ? "A video is already on file; upload only to replace it." : ""}</p>

          <label className="fs-13 mb-16" style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-start", cursor: "pointer" }}>
            <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} style={{ marginTop: 3 }} data-testid="admin-kyc-confirm" />
            <span>
              I have watched the video and the artist reads and accepts these declarations:
              <ul className="text-muted fs-12" style={{ margin: "4px 0 0 18px" }}>
                {record.declaration_texts.map((text) => <li key={text}>{text}</li>)}
              </ul>
            </span>
          </label>

          <h3 className="fs-16 fw-700 mb-8">Documents</h3>
          <div className="grid-2 mb-12">
            {DOC_FIELDS.map(([key, label]) => (
              <div key={key}>
                <label className="field-label" htmlFor={`admin-kyc-doc-${key}`}>
                  {label}{record.documents.includes(key) ? " · on file" : ""}
                </label>
                <input id={`admin-kyc-doc-${key}`} type="file" accept="image/*,application/pdf" onChange={(event) => chooseDoc(key, event.target.files?.[0])} data-testid={`admin-kyc-doc-${key}`} />
                {docs[key] && <div className="text-muted fs-11 mt-4">Selected: {docs[key].name}</div>}
              </div>
            ))}
          </div>

          <h3 className="fs-16 fw-700 mb-8">Identity details <span className="text-muted fs-12 fw-400">(optional)</span></h3>
          <div className="grid-2 mb-12">
            {field("full_name", record.full_name ? `Full name · on file: ${record.full_name}` : "Full name", { maxLength: 120 })}
            {field("dob", record.dob ? `Date of birth · on file: ${record.dob}` : "Date of birth", { type: "date" })}
            {field("aadhaar_number", record.aadhaar_number_masked ? `Aadhaar number · on file: ${record.aadhaar_number_masked}` : "Aadhaar number", { inputMode: "numeric", maxLength: 14 })}
            {field("pan_number", record.pan_number ? `PAN number · on file: ${record.pan_number}` : "PAN number", { maxLength: 10 })}
          </div>
          {field("note", "Note for the audit log (optional)", { maxLength: 1000, placeholder: "e.g. Video received on WhatsApp on 4 Nov" })}

          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginTop: "var(--space-4)" }}>
            <button type="submit" className="btn btn-gold" disabled={saving} data-testid="admin-kyc-save">
              {saving ? (progress ? `Uploading video… ${progress}%` : "Saving…") : "Save KYC"}
            </button>
            <span className="text-muted fs-12">Approve it afterwards from the KYC Queue.</span>
          </div>
        </form>
      )}
    </div>
  );
}
